import { axiosInstance } from '../../helpers'
import {
    AssetDeleteDocumentResponse,
    AssetUploadDocumentResponse,
    AttachPhotosResponse,
    InventoryAsset,
    InventoryAssetPatchPayload,
    InventoryConfigOptionsResponse,
    PaginatedProductProfiles,
    ProductDocument,
    ProductDocumentType,
    ProductProfile,
    ProductProfileCreatePayload,
    ProductProfilePatchPayload,
    UnitInventoryResponse,
} from './types'

const BASE = '/beta/api/asset-discovery'

export const getUnitInventory = (
    unitId: number,
    options?: { confirmed?: boolean },
) =>
    axiosInstance.get<UnitInventoryResponse>(`${BASE}/assets/unit-inventory/`, {
        params: {
            unit_id: unitId,
            ...(options?.confirmed !== undefined
                ? { confirmed: options.confirmed }
                : {}),
        },
    })

export const patchInventoryAsset = (
    assetId: number,
    payload: InventoryAssetPatchPayload,
) => axiosInstance.patch<InventoryAsset>(`${BASE}/assets/${assetId}/`, payload)

export const confirmInventoryAsset = (
    assetId: number,
    payload: InventoryAssetPatchPayload,
) =>
    axiosInstance.patch<InventoryAsset>(
        `${BASE}/assets/${assetId}/confirm/`,
        payload,
    )

export const attachInventoryAssetPhotos = (assetId: number, files: File[]) => {
    const form = new FormData()
    for (const file of files) {
        form.append('images', file, file.name || 'photo.jpg')
    }
    return axiosInstance.post<AttachPhotosResponse>(
        `${BASE}/assets/${assetId}/attach-photos/`,
        form,
    )
}

export const setPrimaryInventoryPhoto = (assetId: number, photoId: number) =>
    axiosInstance.post<InventoryAsset>(
        `${BASE}/assets/${assetId}/set-primary-photo/`,
        { photo_id: photoId },
    )

export const getInventoryConfigOptions = (assetId: number) =>
    axiosInstance.get<InventoryConfigOptionsResponse>(
        `${BASE}/assets/${assetId}/inventory-config-options/`,
    )

export const deleteInventoryAsset = (assetId: number) =>
    axiosInstance.delete(`${BASE}/assets/${assetId}/`)

export const exportUnitInventory = (unitId: number) =>
    axiosInstance.get<string>(`${BASE}/assets/export/`, {
        params: { unit_id: unitId },
        responseType: 'text',
    })

export const saveInventoryAssetEdits = async (
    assetId: number,
    payload: InventoryAssetPatchPayload,
    options?: {
        useConfirmEndpoint?: boolean
        pendingPhotoFiles?: File[]
    },
): Promise<InventoryAsset> => {
    const patchFn = options?.useConfirmEndpoint
        ? confirmInventoryAsset
        : patchInventoryAsset

    const patchResponse = await patchFn(assetId, payload)
    let asset = patchResponse.data

    if (options?.pendingPhotoFiles && options.pendingPhotoFiles.length > 0) {
        const attachResponse = await attachInventoryAssetPhotos(
            assetId,
            options.pendingPhotoFiles,
        )
        asset = attachResponse.data.asset

        if (
            attachResponse.status === 207 &&
            attachResponse.data.errors.length
        ) {
            throw new PartialAttachPhotosError(
                attachResponse.data.errors,
                asset,
            )
        }
    }

    return asset
}

export class PartialAttachPhotosError extends Error {
    errors: string[]

    asset: InventoryAsset

    constructor(errors: string[], asset: InventoryAsset) {
        super('Some photos failed to attach')
        this.errors = errors
        this.asset = asset
    }
}

export const listProductProfiles = (params: {
    apartment_id: number
    search?: string
    page?: number
    page_size?: number
}) =>
    axiosInstance.get<PaginatedProductProfiles>(`${BASE}/product-profiles/`, {
        params,
    })

export const getProductProfile = (profileId: number) =>
    axiosInstance.get<ProductProfile>(`${BASE}/product-profiles/${profileId}/`)

export const createProductProfile = (payload: ProductProfileCreatePayload) =>
    axiosInstance.post<ProductProfile>(`${BASE}/product-profiles/`, payload)

export const patchProductProfile = (
    profileId: number,
    payload: ProductProfilePatchPayload,
) =>
    axiosInstance.patch<ProductProfile>(
        `${BASE}/product-profiles/${profileId}/`,
        payload,
    )

export const deleteProductProfile = (profileId: number) =>
    axiosInstance.delete(`${BASE}/product-profiles/${profileId}/`)

export const uploadProductDocument = (
    profileId: number,
    file: File,
    options?: { name?: string; document_type?: ProductDocumentType },
) => {
    const form = new FormData()
    form.append('file', file, file.name)
    if (options?.name) {
        form.append('name', options.name)
    }
    if (options?.document_type) {
        form.append('document_type', options.document_type)
    }
    return axiosInstance.post<ProductDocument>(
        `${BASE}/product-profiles/${profileId}/upload-document/`,
        form,
    )
}

export const deleteProductDocument = (profileId: number, documentId: number) =>
    axiosInstance.delete(
        `${BASE}/product-profiles/${profileId}/delete-document/`,
        { data: { document_id: documentId } },
    )

export const uploadAssetProductDocument = (
    assetId: number,
    file: File,
    options?: {
        name?: string
        document_type?: ProductDocumentType
        brand?: string
        model_number?: string
    },
) => {
    const form = new FormData()
    form.append('file', file, file.name)
    if (options?.name) {
        form.append('name', options.name)
    }
    if (options?.document_type) {
        form.append('document_type', options.document_type)
    }
    if (options?.brand) {
        form.append('brand', options.brand)
    }
    if (options?.model_number) {
        form.append('model_number', options.model_number)
    }
    return axiosInstance.post<AssetUploadDocumentResponse>(
        `${BASE}/assets/${assetId}/upload-product-document/`,
        form,
    )
}

export const deleteAssetProductDocument = (
    assetId: number,
    documentId: number,
) =>
    axiosInstance.delete<AssetDeleteDocumentResponse>(
        `${BASE}/assets/${assetId}/delete-product-document/`,
        { data: { document_id: documentId } },
    )

export const suggestProductDocuments = (
    assetId: number,
    options?: { async?: boolean; force?: boolean },
) =>
    axiosInstance.post<InventoryAsset>(
        `${BASE}/assets/${assetId}/suggest-product-documents/`,
        {
            async: options?.async ?? true,
            ...(options?.force !== undefined ? { force: options.force } : {}),
        },
    )

export const dismissProductSuggestion = (
    profileId: number,
    suggestionId: number,
) =>
    axiosInstance.post(
        `${BASE}/product-profiles/${profileId}/dismiss-suggestion/`,
        { suggestion_id: suggestionId },
    )
